import {
  Avatar,
  Group,
  MultiSelect,
  type MultiSelectOption,
  Text,
} from "@assetwatch/ui";
import { assigneeValue } from "../../lib/assigneeTokens";
import { useTeamsUsersQuery } from "../../queries/useTeamsUsersQuery";
import type { WorkOrderAssignment } from "../../queries/useWorkOrderAssignmentMutations";
import { Field } from "../snowflakes/work-orders/WorkOrderDetailLayout";

/** Every user, and every active team, a Work Order can be assigned to. */
export function useAssigneeOptions(): MultiSelectOption[] {
  const teamsUsers = useTeamsUsersQuery();
  const users = teamsUsers.data?.users ?? [];
  return [
    ...users
      .filter(
        (user) => user.role === "Technician" || user.role === "Administrator",
      )
      .map((user) => ({
        value: `user:${user.id}`,
        label: user.name,
      })),
    ...(teamsUsers.data?.teams ?? [])
      .filter((team) => team.isActive)
      .map((team) => ({
        value: `team:${team.id}`,
        label: `${team.name} (team)`,
      })),
  ];
}

/**
 * Options include missing directory entries only while they remain selected,
 * so existing assignments can be removed without offering ineligible users.
 */
function withCurrentAssignees(
  options: MultiSelectOption[],
  assignments: WorkOrderAssignment[],
  selectedValue: string[],
): MultiSelectOption[] {
  const known = new Set(options.map((option) => option.value));
  const missing = assignments
    .filter((assignment) => {
      const token = assigneeValue(assignment);
      return !known.has(token) && selectedValue.includes(token);
    })
    .map((assignment) => ({
      value: assigneeValue(assignment),
      label:
        assignment.kind === "team"
          ? `${assignment.name} (team)`
          : assignment.name,
    }));
  return [...options, ...missing];
}

type WorkOrderAssigneesFieldProps = {
  assignments: WorkOrderAssignment[];
} & (
  | { mode: "view" }
  | {
      mode: "edit";
      /** Selected `user:<id>` / `team:<id>` tokens. */
      value: string[];
      onChange: (value: string[]) => void;
      /** A Technician may add or remove only themselves. */
      selfOnly?: boolean;
      currentUserId?: number | null;
      /**
       * Whether the current user may add themselves. Ignored unless
       * `selfOnly`; they can always remove themselves once assigned.
       */
      canAddSelf?: boolean;
    }
);

/**
 * The "Assigned to" field shared by the Work Orders split view and the Work
 * Order detail page, so the two cannot drift apart. View mode lists the
 * assignees; edit mode is a multi-select of users and teams.
 */
export function WorkOrderAssigneesField(props: WorkOrderAssigneesFieldProps) {
  const directoryOptions = useAssigneeOptions();

  if (props.mode === "view") {
    return (
      <Field label="Assigned to">
        {props.assignments.length > 0 ? (
          <Group gap={8} wrap="wrap">
            {props.assignments.map((assignment) => (
              <Group gap={4} key={assignment.id}>
                <Avatar
                  initials={assignment.initials}
                  size="xs"
                  variant="internal"
                />
                <Text>{assignment.name}</Text>
              </Group>
            ))}
          </Group>
        ) : (
          <Text>Unassigned</Text>
        )}
      </Field>
    );
  }

  const {
    assignments,
    value,
    onChange,
    selfOnly = false,
    currentUserId = null,
    canAddSelf = true,
  } = props;
  const selfToken = currentUserId === null ? null : `user:${currentUserId}`;
  const allOptions = withCurrentAssignees(directoryOptions, assignments, value);
  const options = selfOnly
    ? allOptions.filter(
        (option) =>
          value.includes(option.value) ||
          (canAddSelf && option.value === selfToken),
      )
    : allOptions;

  function handleChange(next: string[]) {
    if (!selfOnly) {
      onChange(next);
      return;
    }
    // Everything other than the caller stays as it is; only the caller's own
    // chip can be added or removed.
    const locked = value.filter((token) => token !== selfToken);
    const wantsSelf =
      selfToken !== null &&
      next.includes(selfToken) &&
      (canAddSelf || value.includes(selfToken));
    onChange(wantsSelf && selfToken !== null ? [...locked, selfToken] : locked);
  }

  /* @ds-deviation: the POC assignee field shows an avatar or team icon on
     each chip and groups the options into Teams and Users; the DS MultiSelect
     renders plain text chips and one flat option list — proposed resolution:
     promote into the design system (option adornments and option groups). */
  /* @ds-deviation: the DS MultiSelect cannot lock a single chip, so a
     self-only Technician sees a remove control on chips they cannot remove
     and it does nothing — proposed resolution: promote into the design system
     (a per-option locked state that hides the remove control). */
  /* @ds-deviation: the DS MultiSelect chip remove control is labelled just
     "Remove", so a screen reader cannot tell which assignee it removes —
     proposed resolution: promote into the design system (a remove label that
     includes the option label). */
  return (
    <MultiSelect
      description={
        selfOnly ? "You can add or remove only yourself." : undefined
      }
      label="Assigned to"
      onChange={handleChange}
      options={options}
      placeholder="Choose users or teams"
      size="sm"
      value={value}
    />
  );
}
